/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect, useState } from 'react';
import { request } from '@/services/request';
import { AssociableApps, Component, ComponentsData } from 'types/api-data';
import { IDependencies } from 'types/services';

import { useComponents } from '@/hooks/useComponents';

export const useComponentModalData = (componentName: string, type: string) => {
  const {
    state: { activeWorkflows },
    componentsActions,
  } = useComponents();
  const [dependencies, setDependencies] = useState([]);
  const [dependencyLoading, setDependencyLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState<AssociableApps[]>([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState<ComponentsData>(
    []
  );

  const isAgentOrTool = type && ['agent', 'tool'].includes(type.split('.')[0]);

  const fetchAllData = async () => {
    try {
      setDependencyLoading(true);
      const dependenciesResponse = await request.get({
        endpoint: `components/${componentName}/dependencies`,
      });
      setDependencies(dependenciesResponse[0]?.dependencies);

      if (!isAgentOrTool) {
        const appArr = await componentsActions.fetchAssociableApps();
        const filterAssociatedApps = appArr.filter((app: AssociableApps) =>
          dependenciesResponse[0]?.dependencies.some(
            (dep: IDependencies) =>
              dep.dependency_type === 'comprises' &&
              dep.source_name === app.name
          )
        );

        setSelectedApp(filterAssociatedApps);
      } else {
        await componentsActions.fetchActiveComponents();
        const findWorkflow = dependenciesResponse[0]?.dependencies.filter(
          (item: IDependencies) => item.dependency_type === 'consumes'
        );

        const matchedWorkflows =
          findWorkflow && activeWorkflows
            ? activeWorkflows.filter((workflow: Component) =>
                findWorkflow.some(
                  (dep: IDependencies) =>
                    dep.source_name === workflow.component_name
                )
              )
            : [];

        setSelectedWorkflows(matchedWorkflows);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDependencyLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  return {
    dependencies,
    dependencyLoading,
    setDependencies,
    selectedApp,
    setSelectedApp,
    selectedWorkflows,
    setSelectedWorkflows,
  };
};
